import { Controller } from "@hotwired/stimulus"

export default class extends Controller<HTMLElement> {
    static targets = ['input', 'dropdown', 'clear', 'spinner', 'searchIcon']
    static values  = { suggestionsUrl: String }

    declare readonly inputTarget:         HTMLInputElement
    declare readonly dropdownTarget:      HTMLElement
    declare readonly clearTarget:         HTMLButtonElement
    declare readonly spinnerTarget:       HTMLElement
    declare readonly hasSpinnerTarget:    boolean
    declare readonly searchIconTarget:    HTMLElement
    declare readonly hasSearchIconTarget: boolean
    declare suggestionsUrlValue:          string

    private suggestTimer: ReturnType<typeof setTimeout> | null = null
    private activeIdx:    number = -1

    connect(): void {
        document.addEventListener('click', this.onOutsideClick)
        this.dropdownTarget.addEventListener('click',     this.onDropdownClick)
        this.dropdownTarget.addEventListener('mouseover', this.onDropdownHover)
        document.addEventListener('datatable:drawn', this.onDtDrawn)
    }

    disconnect(): void {
        document.removeEventListener('click', this.onOutsideClick)
        this.dropdownTarget.removeEventListener('click',     this.onDropdownClick)
        this.dropdownTarget.removeEventListener('mouseover', this.onDropdownHover)
        document.removeEventListener('datatable:drawn', this.onDtDrawn)
    }

    // ── event handlers wired via data-action ──

    onInput(): void {
        const val = this.inputTarget.value
        this.clearTarget.style.display = val ? '' : 'none'

        if (val.trim()) {
            this.showSpinner()
        } else {
            this.hideSpinner()
        }

        // Apply to DataTables by writing to its own (hidden) search input
        this.applyDtSearch(val.trim())

        if (this.suggestTimer) clearTimeout(this.suggestTimer)
        if (val.trim().length >= 2) {
            this.suggestTimer = setTimeout(() => this.fetchSuggestions(val.trim()), 240)
        } else {
            this.hideDropdown()
        }
    }

    onKeydown(e: KeyboardEvent): void {
        const items = this.dropdownTarget.querySelectorAll<HTMLElement>('.rs-item')
        if (!items.length) return
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            this.activeIdx = Math.min(this.activeIdx + 1, items.length - 1)
            this.highlightItems(items)
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            this.activeIdx = Math.max(this.activeIdx - 1, -1)
            this.highlightItems(items)
        } else if (e.key === 'Enter' && this.activeIdx >= 0) {
            e.preventDefault()
            items[this.activeIdx].click()
        } else if (e.key === 'Escape') {
            this.hideDropdown()
        }
    }

    clearSearch(): void {
        this.inputTarget.value = ''
        this.clearTarget.style.display = 'none'
        this.hideDropdown()
        this.hideSpinner()
        this.applyDtSearch('')
        this.inputTarget.focus()
    }

    // ── private ──

    private onDtDrawn = (): void => {
        this.hideSpinner()
    }

    private showSpinner(): void {
        if (this.hasSpinnerTarget)    this.spinnerTarget.style.display    = ''
        if (this.hasSearchIconTarget) this.searchIconTarget.style.display = 'none'
    }

    private hideSpinner(): void {
        if (this.hasSpinnerTarget)    this.spinnerTarget.style.display    = 'none'
        if (this.hasSearchIconTarget) this.searchIconTarget.style.display = ''
    }

    private applyDtSearch(val: string): void {
        // Writes to the DataTables built-in search input (hidden by CSS) and
        // dispatches an input event so DataTables fires its own debounced search.
        const dtInput = document.querySelector<HTMLInputElement>(
            '#children-datatable input[type="search"]'
        )
        if (!dtInput) return
        dtInput.value = val
        dtInput.dispatchEvent(new Event('input', { bubbles: true }))
    }

    private onOutsideClick = (e: MouseEvent): void => {
        if (!this.element.contains(e.target as Node)) this.hideDropdown()
    }

    private onDropdownClick = (e: MouseEvent): void => {
        const item = (e.target as HTMLElement).closest<HTMLElement>('.rs-item')
        if (!item) return
        const name = item.dataset.name || ''
        this.inputTarget.value = name
        this.clearTarget.style.display = name ? '' : 'none'
        this.hideDropdown()
        this.applyDtSearch(name)
    }

    private onDropdownHover = (e: MouseEvent): void => {
        const item = (e.target as HTMLElement).closest<HTMLElement>('.rs-item')
        if (!item) return
        const items = this.dropdownTarget.querySelectorAll<HTMLElement>('.rs-item')
        this.activeIdx = Array.from(items).indexOf(item)
        this.highlightItems(items)
    }

    private highlightItems(items: NodeListOf<HTMLElement>): void {
        items.forEach((el, i) => el.classList.toggle('active', i === this.activeIdx))
    }

    private async fetchSuggestions(q: string): Promise<void> {
        try {
            const url = (this.suggestionsUrlValue || '/registers/api-suggestions') + '?q=' + encodeURIComponent(q)
            const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
            if (!res.ok) return
            const data: Array<{id: number; name: string; lastName: string; legacyId: string | null}> = await res.json()
            this.renderDropdown(data, q)
        } catch {
            this.hideDropdown()
        }
    }

    private renderDropdown(items: Array<{id: number; name: string; lastName: string; legacyId: string | null}>, q: string): void {
        if (!items.length) { this.hideDropdown(); return }
        this.activeIdx = -1
        this.dropdownTarget.innerHTML = items.map(item => {
            const full  = item.name + ' ' + item.lastName
            const hl    = this.highlight(full, q)
            const badge = item.legacyId
                ? `<span class="badge bg-secondary fw-normal ms-2" style="font-size:10px">#${this.esc(item.legacyId)}</span>`
                : ''
            return `<div class="rs-item" data-name="${this.escAttr(full)}">${hl}${badge}</div>`
        }).join('')
        this.dropdownTarget.style.display = 'block'
    }

    private hideDropdown(): void {
        this.dropdownTarget.style.display = 'none'
        this.dropdownTarget.innerHTML     = ''
        this.activeIdx = -1
    }

    private highlight(text: string, q: string): string {
        const safe  = this.esc(text)
        const safeQ = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        return safe.replace(new RegExp(`(${safeQ})`, 'gi'), '<mark class="bg-warning-subtle px-0">$1</mark>')
    }

    private esc(s: string): string {
        return s.replace(/[<>&"]/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;'} as Record<string,string>)[c])
    }

    private escAttr(s: string): string {
        return s.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
    }
}
